/**
 * Custom Modal Component
 * Extends Ant Design Modal with additional features
 */

import { Modal as AntModal, ModalProps as AntModalProps } from 'antd';
import { ExclamationCircleOutlined } from '@ant-design/icons';
import classNames from 'classnames';
import './CustomModal.scss';

export interface CustomModalProps extends AntModalProps {
  /** Modal size */
  size?: 'small' | 'medium' | 'large' | 'full';
  /** Show close button */
  showClose?: boolean;
}

/**
 * CustomModal Component
 */
const CustomModalComponent: React.FC<CustomModalProps> = ({
  size = 'medium',
  showClose = true,
  className,
  width,
  ...rest
}) => {
  const modalClasses = classNames(
    'custom-modal',
    `custom-modal--${size}`,
    className
  );

  // Calculate width based on size
  const modalWidth = width || {
    small: 400,
    medium: 600,
    large: 900,
    full: '90vw',
  }[size];

  return (
    <AntModal
      {...rest}
      width={modalWidth}
      className={modalClasses}
      closable={showClose}
      centered
    />
  );
};

// Create compound component with static methods
export const CustomModal = Object.assign(CustomModalComponent, {
  confirm: (config: Parameters<typeof AntModal.confirm>[0]) => {
    return AntModal.confirm({
      icon: <ExclamationCircleOutlined />,
      okText: 'Confirm',
      cancelText: 'Cancel',
      centered: true,
      ...config,
    });
  },
  success: AntModal.success,
  error: AntModal.error,
  warning: AntModal.warning,
  info: AntModal.info,
});

export default CustomModal;

